import {
  Button,
  Stack,
  Switch,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTRPC } from "../../../trpc";
import type { ChangeEvent } from "react";
import { useCallback } from "react";
import { ActionCard } from "../../../components/ActionCard";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { FormattedMessage, useIntl } from "react-intl";
import { DeleteAccountModal } from "./components/DeleteAccountModal";
import { ChangeAccountModal } from "./components/ChangeAccountModal";
import { ChangeLogoDropzone } from "./components/ChangeLogoDropzone";
import { assertAccountSelected, useIsServiceAccount, useUser } from "../../../hooks/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const AccountConfigurations = () => {
  const intl = useIntl();
  const user = useUser();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const isServiceAccount = useIsServiceAccount();

  assertAccountSelected(user);

  const { data: account } = useQuery(trpc.getCurrentAccount.queryOptions());
  const { mutate: updateAccount, isPending } = useMutation(trpc.updateCurrentAccount.mutationOptions());

  const {
    currentAccount: {
      name,
    },
  } = user;

  const [
    openedChangeAccountName,
    {
      open,
      close,
    },
  ] = useDisclosure(false);

  const [
    openedCloseAccount,
    {
      open: openCloseAccount,
      close: closeCloseAccount,
    },
  ] = useDisclosure(false);

  const handleChangeSupportEdit = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    updateAccount({
      allowSupportEdit: event.target.checked,
    }, {
      onSuccess: (updatedAccount) => {
        queryClient.setQueryData(trpc.getCurrentAccount.queryKey(), updatedAccount);
        void queryClient.invalidateQueries(trpc.getCurrentUser.queryFilter());
      },
    });
  }, [updateAccount, queryClient]);

  return (
    <Stack gap={16} h="100%">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Manage account", id: "navigation.manageAccount" })}
        current={intl.formatMessage({ defaultMessage: "Configurations", id: "navigation.configurations" })}
        description={intl.formatMessage({ defaultMessage: "Edit your account name and other configurations.", id: "text.configurationsDescription" })}
      />
      <ActionCard
        title={intl.formatMessage({ defaultMessage: "Account name", id: "title.accountName" })}
        subTitle={name}
      >
        <Button onClick={open} variant="primary">
          <FormattedMessage defaultMessage="Change name" id="button.changeName" />
        </Button>
      </ActionCard>

      {isServiceAccount && (
        <ActionCard
          title={intl.formatMessage({ defaultMessage: "Account logo", id: "title.accountLogo" })}
          subTitle={intl.formatMessage({ defaultMessage: "Upload or change the logo for this account", id: "text.accountLogoDescription" })}
        >
          <ChangeLogoDropzone
            accountName={user.currentAccount.name}
            logo={account?.logo ?? null}
          />
        </ActionCard>
      )}

      <ActionCard
        title={intl.formatMessage({ defaultMessage: "Editing permission", id: "title.editingPermission" })}
        subTitle={intl.formatMessage({
          defaultMessage: "Grant product support edit access to this account",
          id: "text.editingPermissionDescription",
        })}
      >
        <Switch
          disabled={isPending}
          checked={account?.allowSupportEdit}
          onChange={handleChangeSupportEdit}
        />
      </ActionCard>

      <ChangeAccountModal open={openedChangeAccountName} onClose={close} />
      {!isServiceAccount && (
        <ActionCard
          title={intl.formatMessage({ defaultMessage: "Account", id: "title.account" })}
          subTitle={intl.formatMessage({
            defaultMessage: "Current number of users: {count} ",
            id: "text.accountCountDescription",
          }, { count: user.accounts.length })}
        >
          <Button variant="danger" onClick={openCloseAccount}>
            {intl.formatMessage({ defaultMessage: "Close this account", id: "button.deleteThisAccount" })}
          </Button>
        </ActionCard>
      )}

      <DeleteAccountModal open={openedCloseAccount} onClose={closeCloseAccount} />
    </Stack>
  );
};
